import {useEffect, useMemo, useState} from 'react';

import PropTypes from 'prop-types';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import HeroBannerNativeVideo from '@amblique/king-living/app/components/hero-banner-background-video';
import {isRealTouchDevice} from '@amblique/king-living/app/utils/is-real-touch-device';
import {normalizeVideoId} from '@amblique/king-living/app/utils/video-utils';

const DIRECT_VIDEO_FILE_REGEX = /\.(?:mp4|webm|ogg|mov|avi|mkv|m4v)(?:\?|$)/i;

const HERO_VIDEO_OPTIONS = {
  autoplay: true,
  controls: false,
  loop: true,
  mute: true,
  muted: true,
  playsinline: true,
  readonly: true,
  autopilot: false,
  size: 'cover',
};

function ensureHttpsUrl(url) {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();

  return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
}

function resolveHeroVideoSrc(component) {
  const candidates = [component?.videoAsset?.url, normalizeVideoId(component?.videoIdOrUrl)];

  for (const candidate of candidates) {
    const url = ensureHttpsUrl(candidate);

    if (url && DIRECT_VIDEO_FILE_REGEX.test(url)) {
      return url;
    }
  }

  return null;
}

export function withHeroBannerVideoOptions(component) {
  const cmsOptions =
    component?.options && typeof component.options === 'object' && !Array.isArray(component.options) ?
      component.options :
      {};

  return {
    ...component,
    presets: component?.presets || 'hero',
    options: {
      ...cmsOptions,
      ...HERO_VIDEO_OPTIONS,
    },
  };
}

/**
 * Hero banner background video — scoped iOS/Android fix for Contentful `.mp4` assets.
 */
export const HeroBannerBackgroundVideo = ({component, ...mediaProps}) => {
  const directSrc = useMemo(() => resolveHeroVideoSrc(component), [component]);
  const heroComponent = useMemo(() => withHeroBannerVideoOptions(component), [component]);
  const [useNative, setUseNative] = useState(false);

  useEffect(() => {
    setUseNative(isRealTouchDevice() && Boolean(directSrc));
  }, [directSrc]);

  if (useNative && directSrc) {
    return <HeroBannerNativeVideo containerProps={mediaProps} src={directSrc} />;
  }

  return <Component component={heroComponent} {...mediaProps} />;
};

HeroBannerBackgroundVideo.displayName = 'HeroBannerBackgroundVideo';
HeroBannerBackgroundVideo.propTypes = {
  component: PropTypes.object.isRequired,
};
